import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import { getTreinamentosDetalhe } from "../../components/Artigos/articlesService";
import { AuthContext } from "../../contexts/authContext";

// Adicionando um ícone de "carregando" para um feedback visual melhor
import { AiOutlineLoading3Quarters } from 'react-icons/ai'; 
import api from "../../services/api";

// Tipos: Mantenha como está
type Material = {
  id: number;
  nome: string;
  tipo: string;
  caminho: string;
};

type Treinamento = {
  id: number;
  titulo: string;
  descricao: string;
  carga_horaria: number;
  materiais: Material[];
};


export default function TreinamentoDetalhe() {
  const [treinamento, setTreinamento] = useState<Treinamento | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useContext(AuthContext);

  const accessToken = localStorage.getItem("@seculos:token");
  const token = accessToken ? JSON.parse(accessToken).accessToken : null;

  // Lógica de `useEffect` e `handleVideoEnd` permanece a mesma
  useEffect(() => {
    const getTreinamento = async () => {
      if (!token) {
        console.warn("Token inválido");
        setLoading(false);
        return;
      }

      if (!id) {
        console.warn("ID do treinamento não encontrado");
        navigate("/painel/treinamento");
        return;
      }

      try {
        const data = await getTreinamentosDetalhe(Number(id), token);
        setTreinamento(data);
      } catch (err) {
        console.error("Erro ao carregar treinamento:", err);
      } finally {
        setLoading(false);
      }
    };
    
    getTreinamento();

  }, [id, token, navigate]);

  function handleVideoEnd() {
    if (!treinamento) return;
    const nextIndex = currentIndex + 1;

    if (nextIndex < treinamento.materiais.length) {
      setCurrentIndex(nextIndex);
      // Ajuste o cálculo de progresso para refletir a nova aula iniciada
      setProgress(((nextIndex) / treinamento.materiais.length) * 100); 
      setPlaying(true);
    } else {
      setProgress(100);
      setPlaying(false);
    }
  };
  
  // Função para simular a emissão de certificado
  const emitirCertificado = () => {
    alert("🎉 Certificado emitido com sucesso! Parabéns!");
  };

  // --- Estados de Carregamento e Erro Aprimorados ---
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-blue-600">
        <AiOutlineLoading3Quarters className="animate-spin text-4xl mb-4" />
        <p className="text-lg font-medium">Carregando detalhes do treinamento...</p>
      </div>
    );
  }

  if (!treinamento) {
    return (
      <div className="p-10 text-center bg-white rounded-xl shadow-lg m-10 border border-red-200">
        <h2 className="text-xl font-bold text-red-600 mb-2">Treinamento Não Encontrado</h2>
        <p className="text-gray-600">O ID informado não corresponde a um treinamento válido.</p>
        <button 
            onClick={() => navigate("/painel/treinamentos")}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
        >
            Voltar para Treinamentos
        </button>
      </div>
    );
  }

  const materiaisValidos = treinamento.materiais.filter(
    (m) => m.tipo === "video",
  );
  const videoAtual = materiaisValidos[currentIndex];

  const videoBaseUlr = `http://localhost:5452/uploads/treinamento/${videoAtual.caminho}`;

  // --- Estrutura Principal Aprimorada ---
  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50 overflow-hidden">
      
      {/* Sidebar - Lista de Aulas */}
      {/* w-full em mobile, w-1/3 ou w-1/4 em desktop */}
      <aside className="w-64 lg:w-96 bg-white border-r border-gray-100 flex flex-col shadow-xl lg:shadow-md overflow-y-auto">
        
        {/* Cabeçalho da Sidebar */}
        <div className="p-6 border-b bg-white">
          <h1 className="text-xl font-bold text-gray-800">{treinamento.titulo}</h1>
          <p className="text-sm text-gray-500 mt-1">{treinamento.descricao}</p>
          <p className="text-xs text-gray-400 mt-2">Carga Horária: **{treinamento.carga_horaria}h**</p>
        </div>

        {/* Lista de aulas */}
        <div className="p-4 flex-1 overflow-y-auto">
          <h3 className="text-sm font-semibold uppercase text-gray-500 mb-3">Conteúdo do Curso</h3>
          {materiaisValidos.map((material, index) => {
            const concluido = index < currentIndex;
            const ativo = index === currentIndex;
            const bloqueado = index > currentIndex; // Tornando o bloqueio explícito
            
            return (
              <button
                key={material.id}
                disabled={bloqueado}
                onClick={() => {
                setCurrentIndex(index);
                setPlaying(true);
                }}
                // Estilos mais modernos e claros para o estado da aula
                className={`w-full text-left p-4 rounded-xl mb-3 flex items-center justify-between transition duration-300 transform hover:scale-[1.01] ${
                  ativo
                    ? "bg-blue-600 text-blue-50 shadow-lg shadow-blue-200" // Aula Atual
                    : concluido
                    ? "bg-white text-green-700 border border-green-200 shadow-sm" // Aula Concluída
                    : "bg-gray-100 text-gray-500 cursor-not-allowed opacity-75" // Aula Bloqueada
                }`}
              >
                <div className="flex flex-col">
                  <span className={`text-sm font-medium ${ativo ? 'text-gray-800' : 'text-gray-800'}`}>{material.nome}</span>
                  <span className={`text-xs mt-1 ${ativo ? 'text-blue-100' : bloqueado ? 'text-gray-400' : 'text-green-600'}`}>
                    {ativo ? 'Aula Atual' : concluido ? 'Concluído' : 'Bloqueado'}
                  </span>
                </div>
                {concluido && <span className="text-xl">✅</span>}
                {ativo && <span className="text-xl">▶️</span>}
                {bloqueado && <span className="text-xl">🔒</span>}
              </button>
            );
          })}
        </div>

        {/* Barra de progresso */}
        <div className="p-6 border-t bg-white">
          <p className="text-sm text-gray-700 font-semibold mb-2">
            Progresso Geral: **{Math.round(progress)}%**
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-500 h-2.5 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </aside>

      {/* Main Content - Player principal */}
      <main className="flex-1 flex flex-col p-6 lg:p-10 overflow-y-auto">
        <div className="flex-1 flex flex-col items-center justify-start">

            {videoAtual ? (
            <div className="w-full h-full max-w-6xl bg-white p-5 lg:p-8 rounded-2xl shadow-2xl border border-gray-100">
                
                {/* Título do Vídeo */}
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">
                {videoAtual.nome}
                </h2>

                {/* Container do Player */}
                <div className="h-full aspect-video rounded-xl overflow-hidden shadow-xl bg-black">
                <ReactPlayer
                    src={videoBaseUlr}
                    controls
                    playing={playing}
                    width="100%"
                    height="100%"
                    config={{ file: { attributes: { controlsList: 'nodownload' } } } as any}
                    onEnded={handleVideoEnd}
                />
                </div>
            </div>
            ) : (
            <div className="w-full max-w-6xl p-10 bg-white rounded-xl shadow-lg text-center">
                <p className="text-xl text-gray-500 font-medium">✨ Conteúdo carregado ou indisponível. ✨</p>
                {/* Sugerir o que fazer se o curso não tiver vídeos */}
                {materiaisValidos.length === 0 && (
                    <p className="text-sm text-gray-400 mt-2">Este treinamento pode ser apenas de leitura ou materiais externos.</p>
                )}
            </div>
            )}
        </div>

        {/* Botão de certificado */}
        <div className="mt-8 flex justify-center">
            {progress >= 99.99 && ( // Use >= 99.99 para evitar problemas de arredondamento
            <button
                onClick={emitirCertificado}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg px-8 py-4 rounded-full shadow-xl transition transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-purple-300"
            >
                Emitir Certificado 🎓
            </button>
            )}
        </div>
      </main>
    </div>
  );
}